import { ReactNode, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import icCreditCard from '@/assets/icons/payment-credit-card.svg';
import icBit from '@/assets/icons/payment-bit.svg';
import icBankManual from '@/assets/icons/payment-bank-manual.svg';
import icCheck from '@/assets/icons/payment-check.svg';
import { isValidIsraeliID } from '@/helpers/israeliIdValidator';
import DonationStep2 from './DonationStep2';
import styles from './PublicDonation.module.css';
import {
  DonationCurrency,
  DonationFormState,
  DonationPageConfig,
  DonationPaymentMethod,
  DonorDetails,
  METHODS_BY_CURRENCY,
} from './types';

const METHOD_ICONS: Record<DonationPaymentMethod, string> = {
  CreditCard: icCreditCard,
  Bit: icBit,
  ManualBankTransfer: icBankManual,
  Check: icCheck,
};

interface Props {
  config: DonationPageConfig;
  initialCurrency?: DonationCurrency;
  onSuccess: () => void;
  renderDonorSearch?: (applyDonor: (donor: Partial<DonorDetails>) => void) => ReactNode;
}

const DEFAULT_DONOR: DonorDetails = {
  firstName: '', lastName: '', israeliId: '', phone: '', email: '', address: '', city: '',
};

const DonationFlow = ({ config, initialCurrency = 'ILS', onSuccess, renderDonorSearch }: Props) => {
  const { t } = useTranslation();
  const [phase, setPhase] = useState<'form' | 'payment'>('form');
  const [error, setError] = useState<string | null>(null);
  const [showDayPicker, setShowDayPicker] = useState(false);
  const [form, setForm] = useState<DonationFormState>({
    amount: 0,
    months: 1,
    chargeDay: new Date().getDate(),
    currency: initialCurrency,
    paymentMethod: METHODS_BY_CURRENCY[initialCurrency][0],
    donor: { ...DEFAULT_DONOR },
  });

  useEffect(() => {
    if (!config) return;
    setForm((prev) => {
      if (prev.amount > 0) return prev;
      const first = config.quickButtons.find((b) => (b.currency || 'ILS') === prev.currency);
      if (!first) return prev;
      return { ...prev, amount: first.amount, months: first.months ?? 1 };
    });
  }, [config]);

  const setCurrency = (currency: DonationCurrency) => {
    setForm((prev) => {
      const allowed = METHODS_BY_CURRENCY[currency];
      const paymentMethod = allowed.includes(prev.paymentMethod) ? prev.paymentMethod : allowed[0];
      return { ...prev, currency, paymentMethod };
    });
  };

  const setMethod = (method: DonationPaymentMethod) => {
    setForm((prev) => ({ ...prev, paymentMethod: method }));
  };

  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const isNedarimMethod = form.paymentMethod === 'CreditCard' || form.paymentMethod === 'Bit';
  const showTaxNote = form.currency === 'ILS' && isNedarimMethod && !form.donor.israeliId.trim();

  const setDonor = (partial: Partial<DonorDetails>) => {
    setForm((prev) => ({ ...prev, donor: { ...prev.donor, ...partial } }));
    const keys = Object.keys(partial);
    setFieldErrors((prev) => {
      const next = { ...prev };
      keys.forEach((k) => delete next[k]);
      return next;
    });
  };

  const methods = METHODS_BY_CURRENCY[form.currency];
  const currencySymbol = form.currency === 'ILS' ? '₪' : '$';
  const filteredButtons = [...config.quickButtons]
    .filter((b) => (b.currency || 'ILS') === form.currency)
    .sort((a, b) => a.amount - b.amount);

  const validate = (): boolean => {
    const errors: Record<string, string> = {};

    if (!form.amount || form.amount <= 0) {
      errors.amount = t('donations.validation.amountRequired');
    }

    if (!form.donor.firstName.trim()) errors.firstName = t('donations.validation.required');
    if (!form.donor.lastName.trim()) errors.lastName = t('donations.validation.required');

    if (form.currency !== 'USD') {
      const id = form.donor.israeliId.trim();
      if (!id) errors.israeliId = t('donations.validation.required');
      else if (!isValidIsraeliID(id)) errors.israeliId = t('donations.validation.invalidIsraeliId');
    }

    const phone = form.donor.phone.trim();
    if (phone && !/^0\d{8,9}$/.test(phone)) errors.phone = t('donations.validation.invalidPhone');

    const email = form.donor.email.trim();
    if (email && !/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(email)) errors.email = t('donations.validation.invalidEmail');

    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = () => {
    if (!validate()) return;
    setPhase('payment');
  };

  if (phase === 'payment') {
    return (
      // ph-mask: פרטי תורם, סכומים ותשלום - ממוסך בהקלטות PostHog (HAT-1081).
      <div className={`${styles.flowWrapper} ph-mask`}>
        <button type="button" className={styles.backLink} onClick={() => { setError(null); setPhase('form'); }}>
          <span className={styles.backArrow}>←</span> {t('backToPrevPage')}
        </button>
        <div className={styles.paymentAmountBanner}>
          <span className={styles.paymentAmountLabel}>
            {form.months > 1 ? t('donations.monthlyAmountLabel') : t('donations.paymentAmountLabel')}
          </span>
          <span>
            {currencySymbol}{form.amount.toLocaleString()}
            {form.months > 1 && <span className={styles.paymentMonthsInfo}> x {form.months}</span>}
          </span>
        </div>
        {form.paymentMethod !== 'Bit' && (
          <p className={styles.paymentSubtitle}>{t(`donations.paymentStepSubtitle.${form.paymentMethod}`)}</p>
        )}
        {error && <div className={styles.errorMessage}>{error}</div>}
        <DonationStep2 config={config} form={form} onSuccess={onSuccess} onError={setError} />
      </div>
    );
  }

  return (
    // ph-mask: פרטי תורם וסכומים - ממוסך בהקלטות PostHog (HAT-1081).
    <div className={`${styles.formPage} ph-mask`}>
      {/* ── AMOUNT ── */}
      <div className={styles.amountSection}>
        {filteredButtons.length > 0 ? (
          <>
            <h3 className={styles.sectionTitle}>{t('donations.yourDonationAmount')}</h3>
            <div className={styles.quickButtons}>
              {filteredButtons.map((btn, idx) => (
                <button
                  key={idx}
                  type="button"
                  className={`${styles.quickButton} ${form.amount === btn.amount && form.months === (btn.months ?? 1) ? styles.quickButtonSelected : ''}`}
                  onClick={() => {
                    setForm((prev) => ({ ...prev, amount: btn.amount, months: btn.months ?? 1 }));
                    setFieldErrors((prev) => { const next = { ...prev }; delete next.amount; return next; });
                  }}
                >
                  <span className={styles.quickButtonAmount}>
                    {currencySymbol}{btn.amount.toLocaleString()}
                    {(btn.months ?? 1) > 1 && ` x ${btn.months}`}
                  </span>
                  {btn.label && <span className={styles.quickButtonLabel}>{btn.label}</span>}
                </button>
              ))}
            </div>
          </>
        ) : null}

        <div className={`${styles.amountInputArea} ${fieldErrors.amount ? styles.amountInputAreaError : ''}`}>
          <span className={styles.amountInputLabel}>
            {filteredButtons.length > 0 ? t('donations.orEnterAmount') : t('donations.enterDonationAmount')}
          </span>
          <div className={styles.amountInputRow}>
            <select
              className={styles.currencySelect}
              value={form.currency}
              onChange={(e) => setCurrency(e.target.value as DonationCurrency)}
            >
              <option value="ILS">₪</option>
              <option value="USD">$</option>
            </select>
            <input
              className={`${styles.amountInput} ${fieldErrors.amount ? styles.amountInputError : ''}`}
              type="number"
              min={1}
              placeholder="0.00"
              value={form.amount || ''}
              onChange={(e) => {
                setForm((prev) => ({ ...prev, amount: Number(e.target.value) }));
                setFieldErrors((prev) => { const next = { ...prev }; delete next.amount; return next; });
              }}
            />
          </div>
          {fieldErrors.amount && <small className={styles.amountError}>{fieldErrors.amount}</small>}

          {form.paymentMethod === 'CreditCard' && (
            <div className={styles.monthsRow}>
              <label className={styles.monthsLabel}>{t('donations.numberOfMonths')}</label>
              <input
                className={styles.monthsInput}
                type="number"
                min={1}
                value={form.months}
                onChange={(e) => setForm((prev) => ({ ...prev, months: Math.max(1, Number(e.target.value) || 1) }))}
                onWheel={(e) => (e.target as HTMLInputElement).blur()}
              />
            </div>
          )}

          {form.paymentMethod === 'CreditCard' && form.months > 1 && (
            <>
              <div className={styles.hkNote}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="12" r="10" /><line x1="12" y1="8" x2="12" y2="12" /><line x1="12" y1="16" x2="12.01" y2="16" />
                </svg>
                {t('donations.standingOrderNote')}
              </div>
              <div className={styles.dayPickerToggle}>
                <button
                  type="button"
                  className={styles.dayPickerToggleBtn}
                  onClick={() => setShowDayPicker((prev) => !prev)}
                >
                  {t('donations.chargeDay')}: {form.chargeDay}
                  <span className={`${styles.dayPickerArrow} ${showDayPicker ? styles.dayPickerArrowOpen : ''}`}>▾</span>
                </button>
                {showDayPicker && (
                  <select
                    className={styles.daySelect}
                    value={form.chargeDay}
                    onChange={(e) => setForm((prev) => ({ ...prev, chargeDay: Number(e.target.value) }))}
                  >
                    {Array.from({ length: 31 }, (_, i) => i + 1).map((d) => (
                      <option key={d} value={d}>{d}</option>
                    ))}
                  </select>
                )}
              </div>
            </>
          )}
        </div>
      </div>

      {/* ── PAYMENT METHOD ── */}
      <div className={styles.section}>
        <h3 className={styles.sectionTitle} style={{ display: 'flex', alignItems: 'baseline' }}>
          {t('donations.paymentMethod')}
          {config.mosadId > 0 && !config.isParentMosad && (
            <span
              className={styles.nedarimBanner}
              onClick={() => window.open(`https://www.matara.pro/nedarimplus/online/?mosad=${config.mosadId}`, '_blank')}
              onKeyDown={(e) => { if (e.key === 'Enter') window.open(`https://www.matara.pro/nedarimplus/online/?mosad=${config.mosadId}`, '_blank'); }}
              role="link"
              tabIndex={0}
              style={{ marginInlineStart: 'auto' }}
            >
              {t('donations.nedarimPlusPrefix')}{' '}
              <span className={styles.nedarimLinkPart}>
                <strong>{t('donations.nedarimPlusLinkText')}</strong>
                {' '}
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ display: 'inline', verticalAlign: 'middle', marginBottom: 4 }}>
                  <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" /><polyline points="15 3 21 3 21 9" /><line x1="10" y1="14" x2="21" y2="3" />
                </svg>
              </span>
            </span>
          )}
        </h3>
        <div className={styles.methodList}>
          {methods.map((m) => (
            <button
              key={m}
              type="button"
              className={`${styles.methodButton} ${form.paymentMethod === m ? styles.methodButtonActive : ''}`}
              onClick={() => setMethod(m)}
            >
              <img src={METHOD_ICONS[m]} alt="" className={styles.methodIcon} />
              <span className={styles.methodLabel}>{t(`donations.methods.${m}`)}</span>
            </button>
          ))}
        </div>
      </div>

      <div className={styles.divider} />

      {/* ── DONOR SEARCH (coordinator only) ── */}
      {renderDonorSearch && (
        <div className={styles.section}>
          <h3 className={styles.sectionTitle}>{t('donations.admin.searchDonor')}</h3>
          {renderDonorSearch(setDonor)}
        </div>
      )}

      {/* ── DONOR DETAILS ── */}
      <div className={styles.section}>
        <h3 className={styles.sectionTitle}>{t('donations.donorDetails')}</h3>
        {showTaxNote && (
          <div className={styles.taxNote}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="12" r="10" />
              <line x1="12" y1="8" x2="12" y2="12" />
              <line x1="12" y1="16" x2="12.01" y2="16" />
            </svg>
            {t('donations.taxCreditWarningMessage')}
          </div>
        )}
        <p className={styles.requiredNote}>{t('donations.requiredFieldsNote')}</p>
        <div className={styles.fieldsGrid}>
          <div className={styles.field}>
            <label>{t('firstName')} <span className={styles.requiredMark}>*</span></label>
            <input className={fieldErrors.firstName ? styles.fieldInputError : ''} value={form.donor.firstName} onChange={(e) => setDonor({ firstName: e.target.value })} />
            {fieldErrors.firstName && <small className={styles.fieldError}>{fieldErrors.firstName}</small>}
          </div>
          <div className={styles.field}>
            <label>{t('lastName')} <span className={styles.requiredMark}>*</span></label>
            <input className={fieldErrors.lastName ? styles.fieldInputError : ''} value={form.donor.lastName} onChange={(e) => setDonor({ lastName: e.target.value })} />
            {fieldErrors.lastName && <small className={styles.fieldError}>{fieldErrors.lastName}</small>}
          </div>
          {form.currency !== 'USD' && (
            <div className={styles.field}>
              <label>{t('donations.israeliId')} <span className={styles.requiredMark}>*</span></label>
              <input className={fieldErrors.israeliId ? styles.fieldInputError : ''} value={form.donor.israeliId} onChange={(e) => setDonor({ israeliId: e.target.value })} />
              {fieldErrors.israeliId && <small className={styles.fieldError}>{fieldErrors.israeliId}</small>}
            </div>
          )}
          <div className={styles.field}>
            <label>{t('phoneNumber')}</label>
            <input className={fieldErrors.phone ? styles.fieldInputError : ''} value={form.donor.phone} onChange={(e) => setDonor({ phone: e.target.value })} />
            {fieldErrors.phone && <small className={styles.fieldError}>{fieldErrors.phone}</small>}
          </div>
          <div className={styles.field}>
            <label>{t('email')}</label>
            <input className={fieldErrors.email ? styles.fieldInputError : ''} type="email" value={form.donor.email} onChange={(e) => setDonor({ email: e.target.value })} />
            {fieldErrors.email && <small className={styles.fieldError}>{fieldErrors.email}</small>}
          </div>
          <div className={styles.field}>
            <label>{t('donations.city')}</label>
            <input value={form.donor.city} onChange={(e) => setDonor({ city: e.target.value })} />
          </div>
          <div className={styles.field} style={{ gridColumn: '1 / -1' }}>
            <label>{t('donations.address')}</label>
            <input value={form.donor.address} onChange={(e) => setDonor({ address: e.target.value })} />
          </div>
        </div>
      </div>

      <button type="button" className={styles.submitBtn} onClick={handleSubmit}>
        {t('donations.proceed')}
      </button>

    </div>
  );
};

export default DonationFlow;
